// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { patch } from '@ngxs/store/operators';

// Services
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';
import { JobCommentService } from '../services/job-comment.service';
import { CoreService } from '../../../shared/services';
import { DialogService } from '../../../shared/services/dialog.service';

// Models
import { JobCommentDefaultResponseAllDto } from '../models/job-comment-default-response-all-dto';
import { AppCleanup } from '../../../shared/models';

// Store
import { CommentDataActions } from './comment.action';

// Enums
import { StatusType } from '../../../shared/enums';
import { ConfirmDialogModel } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UtilityHelper } from '../../../shared/helpers';

export interface CommentDataStateModel {
	item: JobCommentDefaultResponseAllDto;
	loaded: boolean;
}

const defaultState: CommentDataStateModel = {
	item: undefined,
	loaded: false,
};

@State<any>({
	name: 'commentData',
	defaults: defaultState,
})
@Injectable()
export class CommentDataState extends AppCleanup {
	constructor(
		private spinnerService: SpinnerService,
		private jobCommentService: JobCommentService,
		private translateService: TranslateService,
		private coreService: CoreService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(CommentDataActions.GetAll)
	getAll(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}
		this.jobCommentService
			.getCommentGroups()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.SetAll)
	setAll(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.SetAll) {
		let processedData = this.processCommentGroups(action.data);
		ctx.setState({
			item: processedData,
			loaded: true,
		});
	}

	processCommentGroups(commentData) {
		commentData.jobCommentDefaultLinks.forEach((jcdl) => {
			jcdl.jobCommentDefaultLinkActionType = commentData.jobCommentDefaultLinkActionTypes.find((jobCommentDefaultLinkActionType) => jobCommentDefaultLinkActionType.id === jcdl.jobCommentDefaultLinkActionTypeId);
			jcdl.mappedDescription = jcdl.jobCommentDefaultLinkActionType.description.replace('{linkedComment}', `'${jcdl.linkedJobCommentDefault.comment}'`);
			jcdl.mappedDescription = jcdl.mappedDescription.replace('{comment}', `'${jcdl.jobCommentDefault.comment}'`);
		});

		return commentData;
	}

	@Action(CommentDataActions.CreateCommentDefaultGroup)
	createCommentDefaultGroup(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.CreateCommentDefaultGroup) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.createCommentDefaultGroup(action.data.commentDefaultGroup)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultGroupSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultGroupError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.UpdateCommentDefaultGroup)
	updateCommentDefaultGroup(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.UpdateCommentDefaultGroup) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.updateCommentDefaultGroup(action.data.commentDefaultGroup)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultGroupSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultGroupError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.DeleteCommentDefaultGroup)
	deleteCommentDefaultGroup(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.DeleteCommentDefaultGroup) {
		let deleteCommentDefaultGroup = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.jobCommentService
				.deleteCommentDefaultGroup(action.data.commentDefaultGroup)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (commentData) => {
						ctx.dispatch(new CommentDataActions.SetAll(commentData));
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultGroupSuccess(commentData));

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultGroupError());
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteCommentDefaultGroup();
					}
				});
		} else {
			deleteCommentDefaultGroup();
		}
	}

	@Action(CommentDataActions.UpdateCommentDefaultGroupSequence)
	updateCommentDefaultGroupSequence(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.UpdateCommentDefaultGroupSequence) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.updateCommentGroupSequence(action.data.commentDefaultGroups)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentDefaultGroups) => {
					// ctx.dispatch(new CommentDataActions.SetAll(commentData));
					// ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.SEQUENCE_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.SEQUENCE_NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT_GROUP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.CreateCommentDefault)
	createCommentDefault(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.CreateCommentDefault) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.createCommentDefault(action.data.commentDefault)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.UpdateCommentDefault)
	updateCommentDefault(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.UpdateCommentDefault) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.updateCommentDefault(action.data.commentDefault)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.DeleteCommentDefault)
	deleteCommentDefault(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.DeleteCommentDefault) {
		let deleteCommentDefault = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.jobCommentService
				.deleteCommentDefault(action.data.commentDefault)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (commentData) => {
						ctx.dispatch(new CommentDataActions.SetAll(commentData));
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultSuccess(commentData));

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultError());

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteCommentDefault();
					}
				});
		} else {
			deleteCommentDefault();
		}
	}

	@Action(CommentDataActions.CreateCommentDefaultLink)
	createCommentDefaultLink(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.CreateCommentDefaultLink) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.createJobCommentDefaultLink(action.data.commentDefaultLink)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultLinkSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.CreateCommentDefaultLinkError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.UpdateCommentDefaultLink)
	updateCommentDefaultLink(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.UpdateCommentDefaultLink) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.updateJobCommentDefaultLink(action.data.commentDefaultLink)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentData) => {
					ctx.dispatch(new CommentDataActions.SetAll(commentData));
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CommentDataActions.DeleteCommentDefaultLink)
	deleteCommentDefaultLink(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.DeleteCommentDefaultLink) {
		let deleteCommentDefaultLink = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.jobCommentService
				.deleteJobCommentDefaultLink(action.data.commentDefaultLink)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (commentData) => {
						ctx.dispatch(new CommentDataActions.SetAll(commentData));
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultLinkSuccess(commentData));

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						ctx.dispatch(new CommentDataActions.DeleteCommentDefaultLinkError());

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.COMMENT.LINK.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.COMMENT.LINK.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteCommentDefaultLink();
					}
				});
		} else {
			deleteCommentDefaultLink();
		}
	}

	@Action(CommentDataActions.UpdateCommentDefaultSequence)
	updateCommentDefaultSequence(ctx: StateContext<CommentDataStateModel>, action: CommentDataActions.UpdateCommentDefaultSequence) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobCommentService
			.updateCommentSequence(action.data.commentDefaults)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (commentDefaultGroups) => {
					// ctx.dispatch(new CommentDataActions.SetAll(commentData));
					// ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkSuccess(commentData));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.SEQUENCE_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CommentDataActions.UpdateCommentDefaultLinkError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.SEQUENCE_NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.DEFAULT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Selector()
	static isLoaded(state: CommentDataStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static commentData(state: CommentDataStateModel): JobCommentDefaultResponseAllDto {
		return state.item;
	}
}
